import {
  Component,
  ElementRef,
  ViewChild,
  forwardRef,
  computed,
  signal,
  input,
  output,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SoSvgIconUI } from "../so-svg-icon/so-svg-icon.component";

export interface SoFileDropConfig {
  maxSizeMultimedia?: number;
  maxSizeDocs?: number;
  excludeMimeTypes?: string[];
  acceptedExtensions?: string;
}

export interface SoFileValidationError {
  file: File;
  errorType: 'size' | 'type' | 'duplicate';
  message: string;
}

@Component({
  selector: 'so-filedrop-ui',
  standalone: true,
  imports: [CommonModule, SoSvgIconUI],
  templateUrl: './so-filedrop.component.html',
  styleUrl: './so-filedrop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoFiledropUI),
      multi: true
    }
  ]
})
export class SoFiledropUI implements ControlValueAccessor {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  // Modern signal-based inputs
  disabled = input<boolean>(false);
  multiple = input<boolean>(true);
  showMessage = input<boolean>(true);
  showFileList = input<boolean>(true);
  showMaxFileSize = input<boolean>(false);
  maxSizeMultimedia = input<number>(50);
  maxSizeDocs = input<number>(50);
  excludeMimeTypes = input<string[]>([]);
  acceptedExtensions = input<string>('.txt, .xls, .xlsx, .csv, .doc, .docx, .pdf, image/*, video/*');
  idInput = input<string>('filedrop-input');
  dropZoneHeight = input<string>('auto');
  cleanFileNames = input<boolean>(true);
  size = input<'default' | 'small'>('default');
  maxListHeight = input<string>('260px');
  showTitle = input<boolean>(true);
  
  // Text customization
  titleUpload = input<string>('Sube tus archivos');
  dropText = input<string>('Arrastra y suelta tus archivos aquí');
  orText = input<string>('o');
  clickText = input<string>('haz clic aquí para seleccionar');
  btnAcceptText = input<string>('Aceptar');
  addMoreFilesText = input<string>('Agregar más archivos');
  maxMultimediaText = input<string>('Tamaño máximo por archivo multimedia');
  maxDocText = input<string>('Tamaño máximo por documento');
  txtTypeSize = input<string>('MB');

  // Backward compatibility inputs (from SoFileDropZoneComponent)
  validateByModal = input<boolean>(false);
  validateMarca = input<boolean>(false);
  showButtonSave = input<boolean>(false);

  // Outputs
  filesChanged = output<File[]>();
  saveFilesEvent = output<File[]>();
  validationErrors = output<SoFileValidationError[]>();
  fileRemoved = output<{ file: File; index: number }>();

  // Iconos
  iconCircleAdd = input<string>('add_circle');
  iconCheck = input<string>('check_circle');
  iconClose = input<string>('close');
  iconCircleAddSize = input<number>(24);
  iconCheckSize = input<number>(24);
  iconCloseSize = input<number>(20);

  // Internal state
  protected files = signal<File[]>([]);
  protected isDragging = signal<boolean>(false);

  // ControlValueAccessor callbacks
  private onChange: (value: File[]) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed values
  protected effectiveDisabled = computed(() => this.disabled());
  
  protected hasFiles = computed(() => this.files().length > 0);

  protected dropZoneClasses = computed(() => ({
    'so-filedrop': true,
    'so-filedrop--single': !this.multiple(),
    'so-filedrop--dragging': this.isDragging() && !this.effectiveDisabled(),
    'so-filedrop--disabled': this.effectiveDisabled(),
    'so-filedrop--has-files': this.hasFiles(),
    'so-filedrop--small': this.size() === 'small'
  }));

  protected dropZoneStyles = computed(() => {
    const height = this.dropZoneHeight();
    if (height !== 'auto') return { 'min-height': height };
    return !this.multiple() ? { 'min-height': '110px' } : {};
  });

  // ControlValueAccessor implementation
  writeValue(value: File[] | null): void {
    this.files.set(value || []);
  }

  registerOnChange(fn: (value: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled via input signal
  }

  // Drag and Drop handlers
  onDragOver(event: DragEvent): void {
    if (this.effectiveDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    if (this.effectiveDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.effectiveDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (event.dataTransfer?.files) {
      const processedFiles = this.cleanFileNames() 
        ? this.cleanFileNamesArray(Array.from(event.dataTransfer.files))
        : Array.from(event.dataTransfer.files);
      this.handleFiles(processedFiles);
    }
  }

  onFileSelected(event: Event): void {
    if (this.effectiveDisabled()) return;
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const processedFiles = this.cleanFileNames() 
        ? this.cleanFileNamesArray(Array.from(input.files))
        : Array.from(input.files);
      this.handleFiles(processedFiles);
      this.resetNativeInput();
    }
  }

  onZoneClick(): void {
    if (this.effectiveDisabled()) return;
    this.fileInput?.nativeElement.click();
  }

  removeFile(index: number): void {
    const currentFiles = this.files();
    const removedFile = currentFiles[index];
    const newFiles = currentFiles.filter((_, i) => i !== index);
    
    this.files.set(newFiles);
    this.onChange(newFiles);
    this.fileRemoved.emit({ file: removedFile, index });
    if (newFiles.length === 0) {
      this.resetNativeInput();
    }
  }

  saveFiles(): void {
    this.saveFilesEvent.emit(this.files());
  }

  validateModal(): void {
    this.saveFilesEvent.emit(this.files());
    this.clearFiles();
  }

  resetFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    this.clearFiles();
  }

  clearFiles(): void {
    this.files.set([]);
    this.onChange([]);
    this.resetNativeInput();
  }

  // File handling
  private handleFiles(newFiles: File[]): void {
    const errors: SoFileValidationError[] = [];
    const currentFiles = this.files();
    const validFiles: File[] = [];

    newFiles.forEach(file => {
      const maxSize = this.getMaxSizeForFile(file);
      const exceedsMaxSize = file.size > maxSize * 1024 * 1024;
      const isExcluded = this.isExcludedFileType(file);
      const isDuplicate = this.isFileDuplicate(file, currentFiles);

      if (exceedsMaxSize) {
        errors.push({
          file,
          errorType: 'size',
          message: `"${file.name}" excede el tamaño máximo de ${maxSize} MB.`
        });
        return;
      }

      if (isExcluded) {
        errors.push({
          file,
          errorType: 'type',
          message: `"${file.name}" tiene un tipo no permitido (${file.type}).`
        });
        return;
      }

      if (isDuplicate) {
        errors.push({
          file,
          errorType: 'duplicate',
          message: `"${file.name}" ya existe en la lista.`
        });
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      this.validationErrors.emit(errors);
    }

    if (validFiles.length > 0) {
      const updatedFiles = this.multiple() 
        ? [...currentFiles, ...validFiles]
        : validFiles.slice(0, 1);
      
      this.files.set(updatedFiles);
      this.onChange(updatedFiles);
      this.onTouched();
      this.filesChanged.emit(updatedFiles);
    }
  }

  private resetNativeInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private getMaxSizeForFile(file: File): number {
    const docMimeTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'text/plain'
    ];
    const isDoc = docMimeTypes.includes(file.type);
    return isDoc ? this.maxSizeDocs() : this.maxSizeMultimedia();
  }

  private isExcludedFileType(file: File): boolean {
    return this.excludeMimeTypes().some(excludedType => 
      file.type.startsWith(excludedType)
    );
  }

  private isFileDuplicate(newFile: File, existingFiles: File[]): boolean {
    return existingFiles.some(existing =>
      existing.name === newFile.name && existing.size === newFile.size
    );
  }

  private cleanFileNamesArray(files: File[]): File[] {
    return files.map(file => {
      const cleanedFileName = file.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9.]/g, '');
      return new File([file], cleanedFileName, { type: file.type });
    });
  }

  // File icon helpers
  getIconForFile(file: File): string {
    if (!file?.name) return 'insert_drive_file';

    const extension = file.name.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        return 'image';
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'webm':
        return 'videocam';
      case 'doc':
      case 'docx':
        return 'description';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'table_chart';
      case 'pdf':
        return 'picture_as_pdf';
      case 'txt':
        return 'article';
      default:
        return 'insert_drive_file';
    }
  }

  getColorForFile(file: File): string {
    if (!file?.name) return 'var(--color-icon, #6b7280)';

    const extension = file.name.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return '--so-danger';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return '--so-success';
      case 'doc':
      case 'docx':
        return '--so-info';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        return '--so-secondary';
      default:
        return '--color-icon';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  trackByFile(index: number, file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }
}
